/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {Navigate} from 'react-router';
import {AppContext} from '../../misc/appContext';
import {Loader, nullify, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {adminEmailSequence, getEmailSequenceCount} from '../../../util/requestSender';
import EmailStep from '../../../models/emailStepModel';
import AdminProcessController, {AdminProcessRef} from '../adminProcess/adminProcessController';
import {EmailFlow, ProcessType} from '../../../util/interfaces';
import Account from '../../../models/accountModel';
import AdminEmailSequence from './adminEmailSequence';
import User from '../../../models/userModel';
import {DASHBOARD_ROUTE} from '../../../util/constants';
import {useTranslation} from 'react-i18next';

const dateFormat = 'YYYY-MM-DD';
export default function AdminEmailSequenceController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const [emailSteps, setEmailSteps] = useState<Array<EmailStep>>([]);
    const [selectedEmailSteps, setSelectedEmailSteps] = useState<Set<string>>(new Set());
    const [fromDate, setFromDate] = useState<IDate | null>(null);
    const [toDate, setToDate] = useState<IDate | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingEmailSteps, setLoadingEmailSteps] = useState(false);
    const [invoiceCount, setInvoiceCount] = useState(0);
    const [loadingInvoiceCount, setLoadingInvoiceCount] = useState(false);
    const processRef = useRef<AdminProcessRef>(null);
    const countController = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!context.userContext.user?.isSuperAdmin()) return;
        loadInvoiceCount();
        return function () {
            abortCountRequest();
        };
    }, [fromDate, toDate]);

    if (!context.userContext.user?.isSuperAdmin()) return <Navigate to={DASHBOARD_ROUTE} />;

    function onSelectEmailStep(emailStepId: string) {
        return function () {
            const selected = new Set(selectedEmailSteps);
            if (selected.has(emailStepId)) selected.delete(emailStepId);
            else selected.add(emailStepId);
            setSelectedEmailSteps(selected);
        };
    }

    function onChangeFromDate(value: IDate | null) {
        setFromDate(value);
    }

    function onChangeToDate(value: IDate | null) {
        setToDate(value);
    }

    function onSubmit() {
        const selectedUser = processRef.current?.getSelectedUser();
        const selectedAccount = processRef.current?.getSelectedAccount();
        if (!selectedUser || !selectedAccount) return;
        setLoading(true);
        adminEmailSequence({
            data: {
                account_id: selectedAccount.id,
                user_id: selectedUser.id,
                email_step_ids: nullify(Array.from(selectedEmailSteps)),
                from_date: fromDate?.format(dateFormat) ?? null,
                to_date: toDate?.format(dateFormat) ?? null
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    showSnackbar(t('admin.emailSequence.startedSnack', 'De verwerking is gestart'));
                    processRef.current?.onLoadProcesses();
                } else showDialog(data.message ?? t('admin.emailSequence.genericError', 'Er is iets fout gegaan.'));
            }
        });
    }

    function onSelectAccount(user: User | null, account: Account | null) {
        loadEmailSteps(user, account);
        loadInvoiceCount(user, account);
    }

    function loadEmailSteps(user: User | null, account: Account | null) {
        if (!user || !account) return setEmailSteps([]);
        setLoadingEmailSteps(true);
        EmailStep.getAdminAll(user.id, account.id, (emailSteps: Array<EmailStep>) => {
            setLoadingEmailSteps(false);
            emailSteps = emailSteps.filter((em: EmailStep) => em.flow !== EmailFlow.CREDIT_INVOICE);
            setEmailSteps(emailSteps);
            const activeSteps = emailSteps.filter((em: EmailStep) => em.active);
            setSelectedEmailSteps(new Set(activeSteps.map((em: EmailStep) => em.id)));
        });
    }

    function loadInvoiceCount(user?: User | null, account?: Account | null) {
        abortCountRequest();
        if (!user) user = processRef.current?.getSelectedUser();
        if (!account) account = processRef.current?.getSelectedAccount();
        if (!user || !account) return setInvoiceCount(0);
        setLoadingInvoiceCount(true);
        countController.current = getEmailSequenceCount({
            data: {
                account_id: account.id,
                user_id: user.id,
                from_date: fromDate?.format(dateFormat) ?? null,
                to_date: toDate?.format(dateFormat) ?? null
            },
            callback: (data: ResponseData) => {
                countController.current = null;
                setLoadingInvoiceCount(false);
                if (data.success) setInvoiceCount(data.count);
            }
        });
    }

    function abortCountRequest() {
        countController.current?.abort();
        countController.current = null;
    }

    return (
        <AdminProcessController
            processType={ProcessType.EMAIL_SEQUENCE}
            ref={processRef}
            onSelectAccount={onSelectAccount}
        >
            <Loader active={loading} />
            <AdminEmailSequence
                onSelectEmailStep={onSelectEmailStep}
                onChangeFromDate={onChangeFromDate}
                onChangeToDate={onChangeToDate}
                onSubmit={onSubmit}
                emailSteps={emailSteps}
                selectedEmailSteps={selectedEmailSteps}
                fromDate={fromDate}
                toDate={toDate}
                loading={loading}
                loadingEmailSteps={loadingEmailSteps}
                invoiceCount={invoiceCount}
                loadingInvoiceCount={loadingInvoiceCount}
            />
        </AdminProcessController>
    );
}
